/* ──────────────────────────────────────────────────────────────────────────
 * RoleSwitcher — the identity pill + dropdown next to the user's name that lets
 * a single account switch smoothly between the processes they run (IPO
 * Workspace / Advisor / Investor / Reseller), start another process, or log out.
 * Self-contained: reads /api/me/context. Scoped under `.role-switcher`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getContext, setRole, logout } from "@/lib/roleContext";
import "./RoleSwitcher.css";

const initialsOf = (s) =>
  (s || "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "US";

export const RoleSwitcher = ({ accent = "#185FA5", currentRole = "", label = "", initials = "" }) => {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { getContext().then(setCtx); }, []);

  const displayLabel = label || ctx?.identity?.company || ctx?.name || ctx?.identity?.advisorFirm || "My account";
  const displayInitials = initials || initialsOf(displayLabel);
  const roles = ctx?.roles || [];

  const switchTo = async (role) => {
    setOpen(false);
    await setRole(role.id);
    navigate(role.path);
  };

  const doLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="role-switcher" data-testid="role-switcher">
      <div className="rs-pill" onClick={() => setOpen((v) => !v)} data-testid="rs-pill">
        <div className="rs-av" style={{ background: accent }}>{displayInitials}</div>
        <div className="rs-name">{displayLabel}</div>
        <i className="ti ti-chevron-down rs-chev" aria-hidden="true" />
      </div>
      {open && (
        <>
          <div className="rs-scrim" onClick={() => setOpen(false)} />
          <div className="rs-menu" data-testid="rs-menu">
            <div className="rs-head">{ctx?.name || "Signed in"}<span>{ctx?.email}</span></div>
            {roles.length > 0 && <div className="rs-lbl">Your processes</div>}
            {roles.map((r) => (
              <button
                key={r.id}
                className={`rs-item ${currentRole === r.id ? "on" : ""}`}
                onClick={() => switchTo(r)}
                data-testid={`rs-role-${r.id}`}
              >
                <span className="rs-item-ic" style={{ background: `${r.accent}15`, color: r.accent }}>
                  <i className={`ti ${r.icon}`} aria-hidden="true" />
                </span>
                <span className="rs-item-txt">
                  <span className="rs-item-h">{r.label}</span>
                  <span className="rs-item-s">{r.sub}</span>
                </span>
                {currentRole === r.id && <i className="ti ti-check rs-item-chk" aria-hidden="true" />}
              </button>
            ))}
            <div className="rs-sep" />
            <button className="rs-item add" onClick={() => { setOpen(false); navigate("/home"); }} data-testid="rs-add-process">
              <span className="rs-item-ic" style={{ background: "#eef2f7", color: "#185FA5" }}><i className="ti ti-plus" aria-hidden="true" /></span>
              <span className="rs-item-txt"><span className="rs-item-h">Start another process</span><span className="rs-item-s">Workspace · Advisor · Investor · Reseller</span></span>
            </button>
            <button className="rs-logout" onClick={doLogout} data-testid="rs-logout"><i className="ti ti-logout" aria-hidden="true" /> Log out</button>
          </div>
        </>
      )}
    </div>
  );
};

export default RoleSwitcher;
